import { useCallback, useEffect, useState } from "react";
import { FaCopy, FaCheck, FaSearch } from "react-icons/fa";
import {
  getUtmLinks,
  deleteUtmLink,
  addUtmLinkPost,
  updateUtmLinkPost,
  deleteUtmLinkPost,
} from "JS/API";

const LIMIT = 10;

const unwrap = (res) => res?.data?.data ?? res?.data;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
};

const styles = {
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "28px",
    backgroundColor: "#ffffff",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "4px",
  },
  cardDesc: {
    fontSize: "13px",
    color: "#9ca3af",
    marginBottom: "20px",
  },
  filterRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  filterInput: {
    padding: "8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#1f2937",
    width: "160px",
  },
  searchBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: "600",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#374151",
    color: "#ffffff",
    cursor: "pointer",
  },
  tableScroll: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "2px solid #e5e7eb",
    color: "#6b7280",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1f2937",
    verticalAlign: "top",
  },
  utmCell: {
    minWidth: "320px",
  },
  utmUrl: {
    fontSize: "13px",
    color: "#1f2937",
    wordBreak: "break-all",
    lineHeight: 1.5,
  },
  postItem: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    padding: "3px 0",
  },
  postItemEditing: {
    display: "block",
    padding: "6px 0",
  },
  postMemo: {
    flex: 1,
    minWidth: 0,
    wordBreak: "break-all",
  },
  postActions: {
    display: "flex",
    gap: "4px",
    flexShrink: 0,
  },
  miniBtn: {
    padding: "0 2px",
    fontSize: "11px",
    border: "none",
    backgroundColor: "transparent",
    color: "#9ca3af",
    cursor: "pointer",
  },
  addPostBtn: {
    marginTop: "2px",
    padding: "0 2px",
    fontSize: "12px",
    border: "none",
    backgroundColor: "transparent",
    color: "#2563eb",
    cursor: "pointer",
  },
  memoCell: {
    minWidth: "220px",
    maxWidth: "300px",
    wordBreak: "break-all",
  },
  postLink: {
    color: "#2563eb",
    textDecoration: "none",
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: "600",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    color: "#374151",
    cursor: "pointer",
    marginRight: "6px",
  },
  deleteBtn: {
    color: "#dc2626",
    borderColor: "#fecaca",
  },
  editInput: {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "13px",
    marginBottom: "6px",
    boxSizing: "border-box",
  },
  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: "14px",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginTop: "16px",
    fontSize: "13px",
    color: "#4b5563",
  },
  pageBtn: {
    padding: "6px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    fontSize: "13px",
  },
};

const UtmLinkHistory = ({ refreshKey, onError }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [applied, setApplied] = useState({ source: "", campaign: "", keyword: "" });
  const [editingPost, setEditingPost] = useState(null);
  const [addingLinkId, setAddingLinkId] = useState(null);
  const [newPost, setNewPost] = useState({ memo: "", post_url: "" });
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(
    async (targetPage, filters) => {
      setLoading(true);
      try {
        const res = await getUtmLinks({ ...filters, page: targetPage, limit: LIMIT });
        if (typeof res !== "object" || !res?.data) {
          onError?.(res);
          return;
        }
        const data = unwrap(res);
        setItems(data?.items || []);
        setTotal(data?.total || 0);
      } finally {
        setLoading(false);
      }
    },
    [onError]
  );

  useEffect(() => {
    load(page, applied);
  }, [page, applied, refreshKey, load]);

  const applyFilters = () => {
    setPage(1);
    setApplied({
      source: sourceFilter.trim(),
      campaign: campaignFilter.trim(),
      keyword: keyword.trim(),
    });
  };

  const handleCopy = async (item) => {
    await copyText(item.url);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const startEditPost = (linkId, post) =>
    setEditingPost({
      linkId,
      postId: post.id,
      memo: post.memo,
      post_url: post.post_url,
    });

  const saveEditPost = async () => {
    if (!editingPost?.memo?.trim()) return;
    const res = await updateUtmLinkPost(editingPost.linkId, editingPost.postId, {
      memo: editingPost.memo.trim(),
      post_url: editingPost.post_url || "",
    });
    if (typeof res !== "object" || !res?.data) {
      onError?.(res);
      return;
    }
    setEditingPost(null);
    load(page, applied);
  };

  const startAddPost = (linkId) => {
    setAddingLinkId(linkId);
    setNewPost({ memo: "", post_url: "" });
  };

  const submitAddPost = async () => {
    if (!newPost.memo.trim()) return;
    const res = await addUtmLinkPost(addingLinkId, {
      memo: newPost.memo.trim(),
      post_url: newPost.post_url.trim(),
    });
    if (typeof res !== "object" || !res?.data) {
      onError?.(res);
      return;
    }
    setAddingLinkId(null);
    load(page, applied);
  };

  const removePost = async (linkId, post) => {
    if (!window.confirm(`배포처 "${post.memo}"를 삭제할까요?`)) return;
    const res = await deleteUtmLinkPost(linkId, post.id);
    if (typeof res !== "object" || !res?.data) {
      onError?.(res);
      return;
    }
    load(page, applied);
  };

  const postsOf = (item) =>
    item.posts?.length
      ? item.posts
      : [{ id: null, memo: item.memo, post_url: item.post_url }];

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.memo}" 링크와 배포처 전체를 삭제할까요?`)) return;
    const res = await deleteUtmLink(item.id);
    if (typeof res !== "object" || !res?.data) {
      onError?.(res);
      return;
    }
    load(page, applied);
  };

  const lastPage = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>만든 링크 내역</div>
      <div style={styles.cardDesc}>
        생성한 UTM 링크를 어디에 뿌렸는지 기록합니다. 총 {total}건
      </div>

      <div style={styles.filterRow}>
        <input
          style={styles.filterInput}
          type="text"
          placeholder="source 필터"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyFilters()}
        />
        <input
          style={styles.filterInput}
          type="text"
          placeholder="campaign 필터"
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyFilters()}
        />
        <input
          style={styles.filterInput}
          type="text"
          placeholder="배포처 메모 검색"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyFilters()}
        />
        <button style={styles.searchBtn} onClick={applyFilters}>
          <FaSearch size={11} />
          검색
        </button>
      </div>

      {loading ? (
        <div style={styles.empty}>로딩 중...</div>
      ) : items.length === 0 ? (
        <div style={styles.empty}>저장된 링크가 없습니다.</div>
      ) : (
        <div style={styles.tableScroll}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>등록일</th>
                <th style={styles.th}>UTM</th>
                <th style={styles.th}>배포처</th>
                <th style={styles.th}>등록자</th>
                <th style={styles.th}>관리</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                    {item.created_at}
                  </td>
                  <td style={{ ...styles.td, ...styles.utmCell }}>
                    <div style={styles.utmUrl}>{item.url}</div>
                  </td>
                  <td style={{ ...styles.td, ...styles.memoCell }}>
                    {postsOf(item).map((post, idx) => (
                      <div
                        key={post.id ?? idx}
                        style={{
                          ...styles.postItem,
                          ...(editingPost?.postId === post.id
                            ? styles.postItemEditing
                            : {}),
                        }}
                      >
                        {editingPost?.postId === post.id ? (
                          <>
                            <input
                              style={styles.editInput}
                              value={editingPost.memo}
                              onChange={(e) =>
                                setEditingPost({ ...editingPost, memo: e.target.value })
                              }
                            />
                            <input
                              style={styles.editInput}
                              value={editingPost.post_url}
                              placeholder="배포 게시물 URL (선택)"
                              onChange={(e) =>
                                setEditingPost({ ...editingPost, post_url: e.target.value })
                              }
                            />
                            <div style={styles.postActions}>
                              <button style={styles.miniBtn} onClick={saveEditPost}>
                                저장
                              </button>
                              <button
                                style={styles.miniBtn}
                                onClick={() => setEditingPost(null)}
                              >
                                취소
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <span style={styles.postMemo}>
                              {post.post_url ? (
                                <a
                                  style={styles.postLink}
                                  href={post.post_url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {post.memo} ↗
                                </a>
                              ) : (
                                post.memo
                              )}
                            </span>
                            {post.id != null && (
                              <span style={styles.postActions}>
                                <button
                                  style={styles.miniBtn}
                                  onClick={() => startEditPost(item.id, post)}
                                >
                                  수정
                                </button>
                                {postsOf(item).length > 1 && (
                                  <button
                                    style={styles.miniBtn}
                                    onClick={() => removePost(item.id, post)}
                                  >
                                    삭제
                                  </button>
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                    {addingLinkId === item.id ? (
                      <div style={{ ...styles.postItem, ...styles.postItemEditing }}>
                        <input
                          style={styles.editInput}
                          value={newPost.memo}
                          placeholder="배포처 메모"
                          onChange={(e) =>
                            setNewPost({ ...newPost, memo: e.target.value })
                          }
                        />
                        <input
                          style={styles.editInput}
                          value={newPost.post_url}
                          placeholder="배포 게시물 URL (선택)"
                          onChange={(e) =>
                            setNewPost({ ...newPost, post_url: e.target.value })
                          }
                        />
                        <div style={styles.postActions}>
                          <button style={styles.miniBtn} onClick={submitAddPost}>
                            추가
                          </button>
                          <button
                            style={styles.miniBtn}
                            onClick={() => setAddingLinkId(null)}
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        style={styles.addPostBtn}
                        onClick={() => startAddPost(item.id)}
                      >
                        + 배포처
                      </button>
                    )}
                  </td>
                  <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                    {item.admin_id}
                  </td>
                  <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                    <button
                      style={styles.actionBtn}
                      onClick={() => handleCopy(item)}
                    >
                      {copiedId === item.id ? (
                        <FaCheck size={11} />
                      ) : (
                        <FaCopy size={11} />
                      )}
                      {copiedId === item.id ? "복사됨" : "복사"}
                    </button>
                    <button
                      style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                      onClick={() => handleDelete(item)}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > LIMIT && (
        <div style={styles.pagination}>
          <button
            style={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </button>
          <span>
            {page} / {lastPage}
          </span>
          <button
            style={styles.pageBtn}
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
};

export default UtmLinkHistory;
